/**
 * Trade Signals v2 — multi-strategy options signals with preset selector.
 *
 * Lives at /trade-signals/v2. Reads from prod-trade-signals-v2 (Plan B1b).
 * The v1 page at /trade-signals stays untouched so subscribers can compare
 * the two engines side-by-side during the transition.
 *
 * Pattern mirrors NotchesPage's preset selector (conservative / balanced /
 * aggressive) — the v2 analyzer generates one signal per preset per setup;
 * the selector simply filters the displayed signals by preset.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import { tradeSignalsService, MacroState, PremiumSnapshot } from '../services/tradeSignalsService';
import { ShouldITradeCard } from './ShouldITradeCard';
import { MacroStressPanel } from './MacroStressPanel';
import { OptionPremiumChart } from './OptionPremiumChart';
import { BacktestPerformanceTab } from './trade-signals/BacktestPerformanceTab';
import { AIBriefTag, AIBriefPanel, BriefForCard } from './trade-signals/AIBriefDisplay';
import { marketRegimeService, MarketRegime } from '../services/marketRegimeService';
import './Dashboard.css';
import { RiskDisclaimer } from './shared/RiskDisclaimer';

// ─── Color palette (matches Notches page) ────────────────────────────────────
const c = {
  bg: '#0f172a',
  card: '#1f2937',
  cardAlt: '#1a2332',
  border: '#374151',
  text: '#f9fafb',
  muted: '#9ca3af',
  dim: '#6b7280',
  green: '#16a34a',
  greenBg: '#16a34a20',
  amber: '#d97706',
  amberBg: '#d9770620',
  red: '#dc2626',
  redBg: '#dc262620',
  purple: '#8b5cf6',
  purpleBg: '#8b5cf620',
  blue: '#3b82f6',
  blueBg: '#3b82f620',
};

type Preset = 'conservative' | 'balanced' | 'aggressive';

// User-facing labels. The internal/back-end key for "Moderate" is "balanced";
// keep the label-to-key mapping in sync with NotchesPage (the established
// convention is "Moderate" in UI, "balanced" in code + signal_ids).
const PRESET_LABEL: Record<Preset, string> = {
  conservative: 'Conservative',
  balanced: 'Moderate',
  aggressive: 'Aggressive',
};

const PRESET_BLURB: Record<Preset, string> = {
  conservative: 'Safety-first. Emphasizes probability-of-profit and liquidity.',
  balanced: 'The default. Empirically-tuned weights across all components.',
  aggressive: 'Higher return targets. Emphasizes return-on-risk and capital efficiency.',
};

// ─── Format helpers ──────────────────────────────────────────────────────────
const fmt = (n: any, d = 2) => (n == null ? '—' : Number(n).toFixed(d));
const fmtDate = (s: string) => {
  try {
    return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return s; }
};
const fmtSignedScore = (n: any) => {
  if (n == null) return '—';
  const v = Number(n);
  return (v >= 0 ? '+' : '') + v.toFixed(3);
};

interface V2Leg {
  type?: string;
  strike?: number | string;
  action?: string;  // 'sell' | 'buy'
  delta?: number | string;
  expiry?: string;
  mark?: number | string;
}

interface V2AlternateLeg {
  action?: string;
  type?: string;
  strike?: number;
  delta?: number;
  mark?: number;
}
interface V2Alternate {
  dte?: number;
  expiration?: string;
  short_strike?: number;
  short_delta?: number;
  legs?: V2AlternateLeg[];
  entry_credit_or_debit?: number;
  capital_at_risk?: number;
  max_profit?: number;
  score?: number;
  pop?: number;
}

interface V2Signal {
  signal_id: string;
  symbol: string;
  strategy?: string;
  strategy_id?: string;
  preset?: Preset | string;
  score?: number | string;
  legs?: V2Leg[];
  entry_credit_or_debit?: number | string;
  capital_at_risk?: number | string;
  max_profit?: number | string;
  expiration?: string;
  dte?: number | string;
  short_strike?: number | string;
  short_delta?: number | string;
  score_components?: {
    pop?: number | string;
    return_on_risk?: number | string;
    capital_efficiency?: number | string;
    liquidity?: number | string;
    trend_alignment?: number | string;
    ivr_advantage?: number | string;
  };
  alternates?: V2Alternate[];
  exit_rules?: {
    profit_target_pct?: number | string;
    time_exit_dte?: number | string;
    naked_short_management?: string;
    convert_trigger_credit_multiple?: number | string;
    convert_width_pct_of_strike?: number | string;
  };
  premium_history?: PremiumSnapshot[];
  signalDate?: string;
  timestamp?: string;  // ISO-8601 UTC when the chain snapshot was taken
}

interface V2Response {
  signals: V2Signal[];
  count: number;
  weekStart?: string;
  weekEnd?: string;
  macro?: MacroState | null;
  version?: string;
}

// ─── Strategy badge styling ──────────────────────────────────────────────────
const STRATEGY_STYLE: Record<string, { color: string; bg: string }> = {
  'Bull Put Spread':                { color: c.green, bg: c.greenBg },
  'Bear Call Spread':               { color: c.red, bg: c.redBg },
  'High IVR Short Put':             { color: c.green, bg: c.greenBg },
  'Low IVR Long Call':              { color: c.blue, bg: c.blueBg },
  'Conservative High-POP Short Put':{ color: c.green, bg: c.greenBg },
  'Covered Call Write':             { color: c.amber, bg: c.amberBg },
  'Iron Condor':                    { color: c.purple, bg: c.purpleBg },
  'Short Strangle':                 { color: c.purple, bg: c.purpleBg },
};

// Compact labels for table view — full names blow out the column width.
const STRATEGY_SHORT: Record<string, string> = {
  'Bull Put Spread':                'BPS',
  'Bear Call Spread':               'BCS',
  'High IVR Short Put':             'SHORT PUT',
  'Low IVR Long Call':              'LONG CALL',
  'Conservative High-POP Short Put':'SHORT PUT',
  'Covered Call Write':             'COV CALL',
  'Iron Condor':                    'IC',
  'Short Strangle':                 'STRANGLE',
};
const shortStrategy = (s: string) => STRATEGY_SHORT[s] || s;

type TabType = 'thisWeek' | 'history' | 'backtest';

const PRESET_STORAGE_KEY = 'tradeSignalsV2.preset';

function loadPreset(): Preset {
  try {
    const v = localStorage.getItem(PRESET_STORAGE_KEY);
    if (v === 'conservative' || v === 'balanced' || v === 'aggressive') return v;
  } catch { /* localStorage blocked */ }
  return 'balanced';
}

function savePreset(p: Preset) {
  try { localStorage.setItem(PRESET_STORAGE_KEY, p); } catch { /* ignore */ }
}

// Helpers for the History tab's date pickers
function getTodayET(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}
function getWeeksAgoET(weeks: number): string {
  const d = new Date();
  d.setDate(d.getDate() - weeks * 7);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function formatDateDisplay(s: string): string {
  if (!s) return '';
  try {
    return new Date(s + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return s; }
}

// ─── Page ────────────────────────────────────────────────────────────────────
export const TradeSignalsV2: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('thisWeek');
  const [signals, setSignals] = useState<V2Signal[]>([]);
  const [weekStart, setWeekStart] = useState<string>('');
  const [weekEnd, setWeekEnd] = useState<string>('');
  const [macro, setMacro] = useState<MacroState | null>(null);
  const [marketRegime, setMarketRegime] = useState<MarketRegime | null>(null);
  const [preset, setPresetState] = useState<Preset>(loadPreset);
  const setPreset = (p: Preset) => { setPresetState(p); savePreset(p); };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSignals, setExpandedSignals] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() => {
    try { return (localStorage.getItem('tradeSignalsV2.viewMode') as 'table' | 'cards') || 'table'; }
    catch { return 'table'; }
  });
  const setViewModePersisted = (m: 'table' | 'cards') => {
    setViewMode(m);
    try { localStorage.setItem('tradeSignalsV2.viewMode', m); } catch {}
  };

  const [showAlternates, setShowAlternates] = useState<boolean>(() => {
    try { return localStorage.getItem('tradeSignalsV2.showAlternates') === '1'; }
    catch { return false; }
  });
  const setShowAlternatesPersisted = (v: boolean) => {
    setShowAlternates(v);
    try { localStorage.setItem('tradeSignalsV2.showAlternates', v ? '1' : '0'); } catch {}
  };
  const [openBriefIds, setOpenBriefIds] = useState<Set<string>>(new Set());
  const toggleBriefOpen = (id: string) => {
    setOpenBriefIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // History tab state
  const [historyStart, setHistoryStart] = useState<string>(getWeeksAgoET(4));
  const [historyEnd, setHistoryEnd] = useState<string>(getTodayET());
  const [historySignals, setHistorySignals] = useState<V2Signal[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // AI Risk Briefings (Notches-style on-demand)
  interface BriefingItem {
    signal_id: string;
    symbol: string;
    strategy: string;
    preset: string;
    briefing: string;
    riskLevel: string;
    watchFor: string;
    generatedAt: string;
    modelVersion: string;
  }
  const [briefings, setBriefings] = useState<BriefingItem[]>([]);
  const [briefingsLoading, setBriefingsLoading] = useState(false);
  const [briefingsRefreshing, setBriefingsRefreshing] = useState(false);
  const [briefingsErr, setBriefingsErr] = useState<string | null>(null);
  const [briefingsGeneratedAt, setBriefingsGeneratedAt] = useState<string | null>(null);

  const briefingsLookup = useMemo(() => {
    const m = new Map<string, BriefingItem>();
    for (const b of briefings) m.set(b.signal_id, b);
    return m;
  }, [briefings]);

  const loadBriefings = async () => {
    if (!token) return;
    try {
      setBriefingsLoading(true);
      setBriefingsErr(null);
      const resp = await tradeSignalsService.getV2RiskBriefings(token);
      setBriefings((resp.briefings || []) as BriefingItem[]);
      if (resp.briefings && resp.briefings.length > 0) {
        setBriefingsGeneratedAt(resp.briefings[0].generatedAt || null);
      } else {
        setBriefingsGeneratedAt(null);
      }
    } catch (err: any) {
      setBriefingsErr(err?.response?.data?.error || err?.message || 'Failed to load AI briefings');
    } finally {
      setBriefingsLoading(false);
    }
  };

  const refreshBriefings = async () => {
    if (!token) return;
    try {
      setBriefingsRefreshing(true);
      setBriefingsErr(null);
      await tradeSignalsService.refreshV2RiskBriefings(token);
      await loadBriefings();
    } catch (err: any) {
      setBriefingsErr(err?.response?.data?.error || err?.message || 'Refresh failed');
    } finally {
      setBriefingsRefreshing(false);
    }
  };

  const loadCurrentSignals = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const resp = (await tradeSignalsService.getV2Signals(token)) as V2Response;
      setSignals(resp.signals || []);
      setWeekStart(resp.weekStart || '');
      setWeekEnd(resp.weekEnd || '');
      setMacro(resp.macro || null);
      // Global macro fix 2026-06-23: prefer the freshest macro across all
      // symbols (returned by /market/regime) over the per-page-bundled snapshot,
      // which can drift between generators.
      marketRegimeService.get(token).then((r) => {
        setMarketRegime(r);
        if (r.macro) setMacro(r.macro as any);
      }).catch(() => {});
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load v2 signals');
    } finally {
      setLoading(false);
    }
  };

  const loadHistorySignals = async () => {
    if (!token) return;
    try {
      setHistoryLoading(true);
      setHistoryError(null);
      // Hit the v2 endpoint directly with date range + version=v2. The
      // typed helper getHistoricalSignals defaults to v1; v2 needs the
      // explicit query param.
      const apiBase = (process.env.REACT_APP_API_URL ||
        'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod');
      const v2HistoryResp = await fetch(
        `${apiBase}/trade-signals?view=history&startDate=${historyStart}&endDate=${historyEnd}&version=v2`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await v2HistoryResp.json();
      setHistorySignals(data.signals || []);
    } catch (err: any) {
      setHistoryError(err?.message || 'Failed to load v2 history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadCurrentSignals();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistorySignals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyStart, historyEnd]);

  useEffect(() => {
    // Briefings are tied to "This Week" signals — load when that tab is
    // active. Refresh button stays available as long as user is on the tab.
    if (activeTab === 'thisWeek') {
      loadBriefings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token]);

  const filteredSignals = useMemo(() => {
    // API already returns ONE row per (symbol, strategy, preset) for
    // the current view (cross-day collapse done in getTradeSignals.js).
    // Frontend only filters by preset and lets the table component
    // handle interactive column sort.
    return signals.filter((s) => (s.preset || 'balanced') === preset);
  }, [signals, preset]);

  const presetCounts = useMemo(() => {
    const counts: Record<string, number> = {
      conservative: 0, balanced: 0, aggressive: 0,
    };
    signals.forEach((s) => {
      const p = s.preset || 'balanced';
      counts[p] = (counts[p] || 0) + 1;
    });
    return counts;
  }, [signals]);

  const toggleExpand = (id: string) => {
    setExpandedSignals((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
              Options Research
              <span style={{ marginLeft: '0.6rem', fontSize: '0.7rem', padding: '0.2rem 0.55rem', borderRadius: 4, background: c.purpleBg, color: c.purple, fontWeight: 700, letterSpacing: '0.04em' }}>
                v2
              </span>
            </h1>
            <p style={{ margin: '0.35rem 0 0', color: c.muted, fontSize: '0.85rem' }}>
              Multi-strategy equity options research — pick your risk preset.
            </p>
          </div>
        </div>

        {/* Should I trade right now? + Macro */}
        {macro && <ShouldITradeCard macro={macro} regime={marketRegime} />}
        {macro && <MacroStressPanel macro={macro} penaltyTarget="bullish" />}

        {/* Tab navigation */}
        <div style={{
          display: 'flex',
          gap: '0.25rem',
          marginTop: '0.75rem',
          borderBottom: `1px solid ${c.border}`,
        }}>
          {([
            { key: 'thisWeek', label: 'This Week' },
            { key: 'history',  label: 'History' },
            { key: 'backtest', label: 'Backtest Performance' },
          ] as { key: TabType; label: string }[]).map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '0.6rem 1.1rem',
                  background: active ? c.purple : 'transparent',
                  color: active ? '#fff' : c.muted,
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: active ? 700 : 500,
                  borderBottom: active ? `2px solid ${c.purple}` : '2px solid transparent',
                  marginBottom: -1,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Preset selector — relevant only on thisWeek + history tabs */}
        {(activeTab === 'thisWeek' || activeTab === 'history') && (
        <div style={{
          background: c.card,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          padding: '0.75rem 1rem',
          marginTop: '0.75rem',
          marginBottom: '0.75rem',
        }}>
          <div style={{ fontSize: '0.78rem', color: c.muted, marginBottom: '0.5rem', fontWeight: 600 }}>
            Risk preset
          </div>
          <div role="radiogroup" aria-label="Trade Signals v2 preset" style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {(['conservative', 'balanced', 'aggressive'] as const).map((p) => {
              const active = preset === p;
              const count = presetCounts[p] || 0;
              return (
                <label
                  key={p}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    fontSize: '0.88rem',
                    color: active ? c.text : c.muted,
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  <input
                    type="radio"
                    name="ts-v2-preset"
                    checked={active}
                    onChange={() => setPreset(p)}
                    style={{ accentColor: c.purple, cursor: 'pointer' }}
                  />
                  {PRESET_LABEL[p]}
                  <span style={{ color: c.dim, fontSize: '0.78rem' }}>({count})</span>
                </label>
              );
            })}
          </div>
          <p style={{ margin: '0.5rem 0 0', color: c.muted, fontSize: '0.78rem', lineHeight: 1.5 }}>
            {PRESET_BLURB[preset]}
          </p>
        </div>
        )}

        {/* ── This Week tab ─────────────────────────────────────────────── */}
        {activeTab === 'thisWeek' && (
          <>
            {/* Week range + refresh */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ color: c.muted, fontSize: '0.85rem' }}>
                {weekStart && weekEnd
                  ? `Week: ${formatDateDisplay(weekStart)} – ${formatDateDisplay(weekEnd)}`
                  : 'Current week'}
              </span>
              <button
                onClick={loadCurrentSignals}
                disabled={loading}
                style={{
                  padding: '0.5rem 1rem',
                  background: loading ? c.dim : c.green,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                }}
              >
                {loading ? 'Refreshing…' : '↻ Refresh Research'}
              </button>
            </div>
            {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: c.muted }}>Loading v2 research…</div>
        ) : error ? (
          <div style={{ padding: '1.5rem', background: c.redBg, border: `1px solid ${c.red}`, borderRadius: 8, color: c.red }}>
            {error}
          </div>
        ) : filteredSignals.length === 0 ? (
          <div style={{
            padding: '2rem',
            background: c.card,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            textAlign: 'center',
            color: c.muted,
          }}>
            <div style={{ fontSize: '1rem', color: c.text, fontWeight: 600, marginBottom: '0.5rem' }}>
              No {PRESET_LABEL[preset]} picks right now
            </div>
            <div style={{ fontSize: '0.85rem' }}>
              v2 only surfaces unusual-activity setups — quiet days produce few or no picks on purpose.
              Try a different risk preset, or check back when the market is more active.
            </div>
          </div>
        ) : (
          <>
            {/* AI Risk Briefings header */}
            <div style={{
              background: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '0.7rem 1rem',
              marginBottom: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              flexWrap: 'wrap',
            }}>
              <div>
                <strong style={{ color: c.text, fontSize: '0.92rem' }}>AI Risk Briefings</strong>
                <div style={{ color: c.muted, fontSize: '0.78rem', marginTop: 2 }}>
                  {briefingsLoading ? 'Loading…'
                    : briefingsErr ? <span style={{ color: c.red }}>{briefingsErr}</span>
                    : briefings.length === 0 ? 'No briefings yet — click Regenerate to run.'
                    : `${briefings.length} top picks have inline briefings — expand any matching card to read`}
                  {briefingsGeneratedAt && (
                    <span style={{ marginLeft: '0.5rem', color: c.dim }}>
                      · generated {new Date(briefingsGeneratedAt).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', month: 'short', day: 'numeric' })} ET
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={refreshBriefings}
                disabled={briefingsRefreshing}
                title="Regenerate AI briefings using the latest top picks"
                style={{
                  padding: '0.45rem 0.9rem',
                  background: briefingsRefreshing ? c.dim : c.purple,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: briefingsRefreshing ? 'wait' : 'pointer',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                }}
              >
                {briefingsRefreshing ? 'Regenerating…' : 'Regenerate'}
              </button>
            </div>

            {/* View-mode toggle + alternates toggle */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '0.5rem', gap: '0.8rem' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: c.muted, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={showAlternates}
                  onChange={(e) => setShowAlternatesPersisted(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Show alternate candidates when row is expanded
              </label>
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                {(['table', 'cards'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setViewModePersisted(m)}
                    style={{
                      padding: '0.3rem 0.7rem', fontSize: '0.75rem', fontWeight: 600,
                      background: viewMode === m ? c.purple : c.cardAlt,
                      color: viewMode === m ? '#fff' : c.muted,
                      border: `1px solid ${viewMode === m ? c.purple : c.border}`,
                      borderRadius: 4, cursor: 'pointer',
                      textTransform: 'capitalize',
                    }}
                  >{m}</button>
                ))}
              </div>
            </div>

            {viewMode === 'table' ? (
              <V2SignalTable
                signals={filteredSignals}
                expanded={expandedSignals}
                onToggle={toggleExpand}
                briefingsLookup={briefingsLookup}
                openBriefIds={openBriefIds}
                onBriefToggle={toggleBriefOpen}
                showAlternates={showAlternates}
              />
            ) : (
              <div style={{ display: 'grid', gap: '0.6rem' }}>
                {filteredSignals.map((sig) => (
                  <V2SignalCard
                    key={sig.signal_id}
                    signal={sig}
                    expanded={expandedSignals.has(sig.signal_id)}
                    onToggle={() => toggleExpand(sig.signal_id)}
                    brief={briefingsLookup.get(sig.signal_id) || null}
                    briefOpen={openBriefIds.has(sig.signal_id)}
                    onBriefToggle={() => toggleBriefOpen(sig.signal_id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
          </>
        )}

        {/* ── History tab ───────────────────────────────────────────────── */}
        {activeTab === 'history' && (
          <div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.85rem', color: c.muted, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                From:
                <input
                  type="date"
                  value={historyStart}
                  max={historyEnd}
                  onChange={(e) => setHistoryStart(e.target.value)}
                  style={{ padding: '0.35rem 0.55rem', background: c.cardAlt, border: `1px solid ${c.border}`, color: c.text, borderRadius: 4, fontSize: '0.85rem' }}
                />
              </label>
              <label style={{ fontSize: '0.85rem', color: c.muted, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                To:
                <input
                  type="date"
                  value={historyEnd}
                  min={historyStart}
                  max={getTodayET()}
                  onChange={(e) => setHistoryEnd(e.target.value)}
                  style={{ padding: '0.35rem 0.55rem', background: c.cardAlt, border: `1px solid ${c.border}`, color: c.text, borderRadius: 4, fontSize: '0.85rem' }}
                />
              </label>
              <button
                onClick={loadHistorySignals}
                disabled={historyLoading}
                style={{ padding: '0.45rem 0.9rem', background: c.purple, color: '#fff', border: 'none', borderRadius: 4, cursor: historyLoading ? 'wait' : 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
              >
                {historyLoading ? 'Loading…' : 'Load'}
              </button>
            </div>

            {historyLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: c.muted }}>Loading v2 history…</div>
            ) : historyError ? (
              <div style={{ padding: '1.5rem', background: c.redBg, border: `1px solid ${c.red}`, borderRadius: 8, color: c.red }}>{historyError}</div>
            ) : historySignals.filter((s) => (s.preset || 'balanced') === preset).length === 0 ? (
              <div style={{ padding: '2rem', background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, textAlign: 'center', color: c.muted }}>
                No {PRESET_LABEL[preset]} picks between {formatDateDisplay(historyStart)} and {formatDateDisplay(historyEnd)}.
              </div>
            ) : (() => {
                const sortedHistory = historySignals
                  .filter((s) => (s.preset || 'balanced') === preset)
                  .sort((a, b) => String(b.signalDate || b.signal_id || '').localeCompare(String(a.signalDate || a.signal_id || '')));
                return viewMode === 'table' ? (
                  <V2SignalTable
                    signals={sortedHistory}
                    expanded={expandedSignals}
                    onToggle={toggleExpand}
                    showAlternates={showAlternates}
                  />
                ) : (
                  <div style={{ display: 'grid', gap: '0.6rem' }}>
                    {sortedHistory.map((sig) => (
                      <V2SignalCard
                        key={sig.signal_id}
                        signal={sig}
                        expanded={expandedSignals.has(sig.signal_id)}
                        onToggle={() => toggleExpand(sig.signal_id)}
                        brief={null}
                        briefOpen={false}
                        onBriefToggle={() => {}}
                      />
                    ))}
                  </div>
                );
              })()}
          </div>
        )}

        {/* ── Backtest Performance tab ──────────────────────────────────── */}
        {activeTab === 'backtest' && token && (
          <div style={{ marginTop: '0.5rem' }}>
            <BacktestPerformanceTab token={token} />
          </div>
        )}

      </div>
      <RiskDisclaimer />
    </div>
  );
};

// ─── Signal-generation timestamp ─────────────────────────────────────────────
// Prices come from the chain snapshot taken at this time — not live quotes.
// Show the time, no fresh/aging/stale labelling: the subscriber can judge
// for themselves whether to re-check the chain before placing the trade.
const QuoteFreshnessTag: React.FC<{ ts: string }> = ({ ts }) => {
  const t = new Date(ts);
  const asOf = t.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric', minute: '2-digit',
    month: 'short', day: 'numeric',
  });
  return (
    <span
      title="Prices reflect the chain snapshot taken at this time. Re-check in TastyTrade before placing the trade."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.15rem 0.5rem',
        borderRadius: 10,
        background: c.cardAlt,
        color: c.muted,
        fontSize: '0.7rem',
        fontWeight: 500,
        cursor: 'help',
        userSelect: 'none',
        border: `1px solid ${c.border}`,
      }}
    >
      Generated {asOf} ET
    </span>
  );
};

// ─── Signal TABLE view ─────────────────────────────────────────────────────
// Compact Notches-style table. Each row is a signal; click chevron to expand
// to the full V2SignalCard detail (legs, score breakdown, AI brief, etc.)
// rendered inline below the row.
// Field accessors used for both sorting and rendering. Returning null
// pushes the row to the bottom regardless of direction.
const sortValue = (sig: V2Signal, key: string): number | string | null => {
  const credit = Number(sig.entry_credit_or_debit || 0);
  const car = Number(sig.capital_at_risk || 0);
  const maxProfit = Number(sig.max_profit || 0);
  const pop = sig.score_components?.pop != null ? Number(sig.score_components.pop) : null;
  const profitTargetPct = Number(sig.exit_rules?.profit_target_pct ?? 0.5);
  const convertMult = Number(sig.exit_rules?.convert_trigger_credit_multiple ?? 1.5);
  const isNakedShort = (sig.exit_rules?.naked_short_management === 'convert');
  switch (key) {
    case 'symbol':   return sig.symbol || '';
    case 'strategy': return sig.strategy || sig.strategy_id || '';
    case 'dte':      return sig.dte != null ? Number(sig.dte) : null;
    case 'strike':   return sig.short_strike != null ? Number(sig.short_strike) : null;
    case 'delta':    return sig.short_delta != null ? Number(sig.short_delta) : null;
    case 'credit':   return credit;
    case 'win':      return pop;
    case 'rr':       return car > 0 ? maxProfit / car : null;
    case 'score':    return sig.score != null ? Number(sig.score) : null;
    case 'target':   return Math.abs(credit) * profitTargetPct;
    case 'stop':     return Math.abs(credit) * (isNakedShort ? convertMult : 2.0);
    case 'exp':      return sig.expiration || '';
    default:         return null;
  }
};

const V2SignalTable: React.FC<{
  signals: V2Signal[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  briefingsLookup?: Map<string, BriefForCard>;
  openBriefIds?: Set<string>;
  onBriefToggle?: (id: string) => void;
  showAlternates?: boolean;
}> = ({ signals, expanded, onToggle, briefingsLookup, openBriefIds, onBriefToggle, showAlternates }) => {
  const [sortKey, setSortKey] = useState<string>('score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const cols = [
    { key: 'symbol',   label: 'SYMBOL',   width: '0.7fr', sortable: true },
    { key: 'strategy', label: 'STRATEGY', width: '1.1fr', sortable: true },
    { key: 'ai',       label: 'AI',       width: '0.5fr', align: 'center' as const, sortable: false },
    { key: 'dte',      label: 'DTE',      width: '0.5fr', align: 'right' as const, sortable: true },
    { key: 'strike',   label: 'STRIKE',   width: '0.7fr', align: 'right' as const, sortable: true },
    { key: 'delta',    label: 'Δ',        width: '0.6fr', align: 'right' as const, sortable: true },
    { key: 'credit',   label: 'CREDIT',   width: '0.7fr', align: 'right' as const, sortable: true },
    { key: 'win',      label: 'WIN %',    width: '0.7fr', align: 'right' as const, sortable: true },
    { key: 'rr',       label: 'R/R',      width: '0.6fr', align: 'right' as const, sortable: true },
    { key: 'score',    label: 'SCORE',    width: '0.6fr', align: 'right' as const, sortable: true },
    { key: 'target',   label: 'TARGET',   width: '0.7fr', align: 'right' as const, sortable: true },
    { key: 'stop',     label: 'STOP',     width: '0.7fr', align: 'right' as const, sortable: true },
    { key: 'exp',      label: 'EXP',      width: '0.7fr', align: 'right' as const, sortable: true },
    { key: 'expand',   label: '',         width: '0.3fr', align: 'center' as const, sortable: false },
  ];
  const gridCols = cols.map((c) => c.width).join(' ');

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // String columns default ascending, numeric default descending —
      // matches the "best-first" expectation for SCORE/CREDIT/WIN etc.
      const isString = key === 'symbol' || key === 'strategy' || key === 'exp';
      setSortDir(isString ? 'asc' : 'desc');
    }
  };

  const sortedSignals = useMemo(() => {
    const arr = [...signals];
    arr.sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      // nulls always last
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp: number;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [signals, sortKey, sortDir]);

  const headerStyle: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: gridCols, gap: '0.5rem',
    padding: '0.5rem 0.8rem', background: c.cardAlt,
    borderTopLeftRadius: 8, borderTopRightRadius: 8,
    borderBottom: `1px solid ${c.border}`,
    fontSize: '0.66rem', fontWeight: 700, color: c.muted,
    letterSpacing: '0.05em', textTransform: 'uppercase',
    position: 'sticky', top: 0, zIndex: 1,
  };

  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={headerStyle}>
        {cols.map((col) => {
          const isActive = col.sortable && sortKey === col.key;
          const arrow = isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
          const align = col.align || 'left';
          return (
            <div
              key={col.key}
              onClick={col.sortable ? () => handleSort(col.key) : undefined}
              style={{
                textAlign: align,
                cursor: col.sortable ? 'pointer' : 'default',
                color: isActive ? c.text : c.muted,
                userSelect: 'none',
              }}
              title={col.sortable ? `Sort by ${col.label}` : undefined}
            >
              {col.label}{arrow}
            </div>
          );
        })}
      </div>
      {sortedSignals.map((sig) => (
        <V2SignalRow
          key={sig.signal_id}
          signal={sig}
          gridCols={gridCols}
          expanded={expanded.has(sig.signal_id)}
          onToggle={() => onToggle(sig.signal_id)}
          brief={briefingsLookup?.get(sig.signal_id) || null}
          briefOpen={openBriefIds?.has(sig.signal_id) || false}
          onBriefToggle={() => onBriefToggle?.(sig.signal_id)}
          showAlternates={!!showAlternates}
        />
      ))}
    </div>
  );
};

const V2SignalRow: React.FC<{
  signal: V2Signal;
  gridCols: string;
  expanded: boolean;
  onToggle: () => void;
  brief: BriefForCard | null;
  briefOpen: boolean;
  onBriefToggle: () => void;
  showAlternates: boolean;
}> = ({ signal, gridCols, expanded, onToggle, brief, briefOpen, onBriefToggle, showAlternates }) => {
  const strategy = signal.strategy || signal.strategy_id || '?';
  const sty = STRATEGY_STYLE[strategy] || { color: c.muted, bg: c.cardAlt };
  const credit = Number(signal.entry_credit_or_debit || 0);
  const isCredit = credit >= 0;
  const car = Number(signal.capital_at_risk || 0);
  const maxProfit = Number(signal.max_profit || 0);
  const rr = car > 0 ? maxProfit / car : 0;
  const pop = signal.score_components?.pop != null ? Number(signal.score_components.pop) : null;
  const profitTargetPct = Number(signal.exit_rules?.profit_target_pct ?? 0.5);
  const convertMult = Number(signal.exit_rules?.convert_trigger_credit_multiple ?? 1.5);
  const isNakedShort = (signal.exit_rules?.naked_short_management === 'convert');
  const targetAmt = Math.abs(credit) * profitTargetPct;
  const stopAmt = Math.abs(credit) * (isNakedShort ? convertMult : 2.0);
  const expDate = signal.expiration ? new Date(signal.expiration + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

  const cellBase: React.CSSProperties = { fontSize: '0.85rem', color: c.text, alignSelf: 'center' };
  const numCell: React.CSSProperties = { ...cellBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <>
      <div
        onClick={onToggle}
        style={{
          display: 'grid', gridTemplateColumns: gridCols, gap: '0.5rem',
          padding: '0.65rem 0.8rem', cursor: 'pointer',
          borderBottom: `1px solid ${c.border}`,
          background: expanded ? c.cardAlt : 'transparent',
        }}
      >
        {/* Symbol */}
        <div style={{ ...cellBase, fontWeight: 700 }}>{signal.symbol}</div>
        {/* Strategy chip (compact label) */}
        <div style={cellBase}>
          <span
            title={strategy}
            style={{
              display: 'inline-block', fontSize: '0.7rem', fontWeight: 600,
              padding: '0.15rem 0.5rem', borderRadius: 4,
              background: sty.bg, color: sty.color,
              whiteSpace: 'nowrap',
            }}
          >{shortStrategy(strategy)}</span>
        </div>
        {/* AI risk pill (Notches style) — clickable opens brief */}
        <div
          style={{ ...cellBase, textAlign: 'center' }}
          onClick={(e) => {
            if (!brief) return;
            e.stopPropagation();
            if (!expanded) onToggle();
            onBriefToggle();
          }}
        >
          {brief ? (() => {
            const risk = (brief as any).riskLevel || 'medium';
            const color = risk === 'low' ? c.green : risk === 'high' ? c.red : c.amber;
            return (
              <span
                title={`AI risk: ${String(risk).toUpperCase()} — click to read briefing`}
                style={{
                  display: 'inline-block',
                  padding: '0.08rem 0.4rem',
                  borderRadius: 4,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  background: `${color}20`,
                  color: color,
                  border: `1px solid ${color}`,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >AI*</span>
            );
          })() : (
            <span style={{ color: c.dim, fontSize: '0.7rem' }}>—</span>
          )}
        </div>
        {/* DTE */}
        <div style={numCell}>{signal.dte ?? '—'}</div>
        {/* Strike */}
        <div style={numCell}>{signal.short_strike != null ? Number(signal.short_strike).toFixed(1) : '—'}</div>
        {/* Delta */}
        <div style={numCell}>{signal.short_delta != null ? Number(signal.short_delta).toFixed(3) : '—'}</div>
        {/* Credit */}
        <div style={{ ...numCell, color: isCredit ? c.green : c.red, fontWeight: 600 }}>
          {isCredit ? '$' : '-$'}{Math.abs(credit).toFixed(2)}
        </div>
        {/* Win % (pop) */}
        <div style={{ ...numCell, fontWeight: 600 }}>{pop != null ? `${(pop * 100).toFixed(1)}%` : '—'}</div>
        {/* R/R */}
        <div style={numCell}>{rr > 0 ? rr.toFixed(2) : '—'}</div>
        {/* Score */}
        <div style={numCell}>{signal.score != null ? Number(signal.score).toFixed(3) : '—'}</div>
        {/* Target $ */}
        <div style={{ ...numCell, color: c.green }}>${targetAmt.toFixed(2)}</div>
        {/* Stop $ */}
        <div style={{ ...numCell, color: c.red }}>${stopAmt.toFixed(2)}</div>
        {/* Exp */}
        <div style={numCell}>{expDate}</div>
        {/* Expand chevron */}
        <div style={{ ...cellBase, textAlign: 'center', color: c.muted, fontSize: '0.7rem' }}>
          {expanded ? '▼' : '▶'}
        </div>
      </div>
      {expanded && (
        <div style={{ background: c.bg, borderBottom: `1px solid ${c.border}`, padding: '0.6rem' }}>
          {showAlternates && signal.alternates && signal.alternates.length > 0 && (
            <V2AlternatesPanel alternates={signal.alternates} winnerCredit={Number(signal.entry_credit_or_debit || 0)} />
          )}
          <V2SignalCard
            signal={signal}
            expanded={true}
            onToggle={onToggle}
            brief={brief}
            briefOpen={briefOpen}
            onBriefToggle={onBriefToggle}
          />
        </div>
      )}
    </>
  );
};

// ─── Alternates panel (shown on row expand when toggle is on) ────────────────
const V2AlternatesPanel: React.FC<{
  alternates: V2Alternate[];
  winnerCredit: number;
}> = ({ alternates, winnerCredit }) => {
  // Show winner's credit sign convention for visual consistency
  const isCreditTrade = winnerCredit >= 0;
  return (
    <div style={{ marginBottom: '0.6rem', background: c.cardAlt, border: `1px solid ${c.border}`, borderRadius: 6, padding: '0.5rem 0.7rem' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: c.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
        Alternate candidates · same strategy, diversified by DTE
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 0.9fr 0.9fr 0.7fr 0.8fr 0.7fr 0.7fr', gap: '0.4rem', fontSize: '0.66rem', fontWeight: 700, color: c.muted, letterSpacing: '0.04em', textTransform: 'uppercase', paddingBottom: '0.3rem', borderBottom: `1px solid ${c.border}` }}>
        <div style={{ textAlign: 'right' }}>DTE</div>
        <div>EXP</div>
        <div style={{ textAlign: 'right' }}>STRIKE</div>
        <div style={{ textAlign: 'right' }}>Δ</div>
        <div style={{ textAlign: 'right' }}>CREDIT</div>
        <div style={{ textAlign: 'right' }}>WIN %</div>
        <div style={{ textAlign: 'right' }}>SCORE</div>
      </div>
      {alternates.map((alt, i) => {
        const credit = Number(alt.entry_credit_or_debit || 0);
        const popPct = alt.pop != null ? `${(Number(alt.pop) * 100).toFixed(1)}%` : '—';
        const expDisplay = alt.expiration
          ? new Date(alt.expiration + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : '—';
        // For multi-leg structures (Strangle, IC, Spreads), render per-leg
        // "TYPE strike (Δ)" lines so the user sees what to actually trade.
        // Single-leg falls back to short_strike/short_delta.
        const legs = (alt.legs && alt.legs.length > 0) ? alt.legs : null;
        const renderStrike = () => {
          if (!legs) {
            return alt.short_strike != null ? Number(alt.short_strike).toFixed(1) : '—';
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
              {legs.map((leg, li) => (
                <div key={li} style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: c.muted, fontSize: '0.65rem', marginRight: '0.3rem' }}>
                    {(leg.type || '').toUpperCase()}
                  </span>
                  {leg.strike != null ? Number(leg.strike).toFixed(1) : '—'}
                </div>
              ))}
            </div>
          );
        };
        const renderDelta = () => {
          if (!legs) {
            return alt.short_delta != null ? Number(alt.short_delta).toFixed(3) : '—';
          }
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
              {legs.map((leg, li) => (
                <div key={li} style={{ fontSize: '0.75rem' }}>
                  {leg.delta != null ? Number(leg.delta).toFixed(3) : '—'}
                </div>
              ))}
            </div>
          );
        };
        return (
          <div
            key={i}
            style={{
              display: 'grid', gridTemplateColumns: '0.6fr 0.9fr 0.9fr 0.7fr 0.8fr 0.7fr 0.7fr',
              gap: '0.4rem', fontSize: '0.8rem', color: c.text,
              padding: '0.45rem 0',
              borderBottom: i === alternates.length - 1 ? 'none' : `1px solid ${c.border}40`,
              fontVariantNumeric: 'tabular-nums',
              alignItems: 'center',
            }}
          >
            <div style={{ textAlign: 'right' }}>{alt.dte ?? '—'}</div>
            <div style={{ color: c.muted }}>{expDisplay}</div>
            <div style={{ textAlign: 'right' }}>{renderStrike()}</div>
            <div style={{ textAlign: 'right' }}>{renderDelta()}</div>
            <div style={{ textAlign: 'right', color: isCreditTrade ? c.green : c.red, fontWeight: 600 }}>
              {isCreditTrade ? '$' : '-$'}{Math.abs(credit).toFixed(2)}
            </div>
            <div style={{ textAlign: 'right', fontWeight: 600 }}>{popPct}</div>
            <div style={{ textAlign: 'right' }}>{alt.score != null ? Number(alt.score).toFixed(3) : '—'}</div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Signal card ─────────────────────────────────────────────────────────────

const V2SignalCard: React.FC<{
  signal: V2Signal;
  expanded: boolean;
  onToggle: () => void;
  brief?: BriefForCard | null;
  briefOpen: boolean;
  onBriefToggle: () => void;
}> = ({ signal, expanded, onToggle, brief, briefOpen, onBriefToggle }) => {
  const strategy = signal.strategy || signal.strategy_id || '?';
  const sty = STRATEGY_STYLE[strategy] || { color: c.muted, bg: c.cardAlt };
  const legs = signal.legs || [];
  const isCredit = Number(signal.entry_credit_or_debit || 0) >= 0;
  const entryAmt = Math.abs(Number(signal.entry_credit_or_debit || 0));
  const car = Number(signal.capital_at_risk || 0);
  const maxProfit = Number(signal.max_profit || 0);
  const dte = signal.dte != null ? Number(signal.dte) : undefined;
  const exitRules = signal.exit_rules || {};

  // Plain-English direction summary
  const directionLabel = legs.length === 0
    ? null
    : (() => {
        const shorts = legs.filter((l) => (l.action || '').toLowerCase() === 'sell');
        const longs  = legs.filter((l) => (l.action || '').toLowerCase() === 'buy');
        if (shorts.length === 1 && longs.length === 1) {
          const t = (shorts[0].type || '').toLowerCase();
          return t.startsWith('p') ? 'Bullish (sell put spread)' : 'Bearish (sell call spread)';
        }
        if (shorts.length === 1 && longs.length === 0) {
          const t = (shorts[0].type || '').toLowerCase();
          return t.startsWith('p') ? 'Bullish (short put)' : 'Bearish (short call)';
        }
        if (shorts.length === 0 && longs.length === 1) {
          const t = (longs[0].type || '').toLowerCase();
          return t.startsWith('c') ? 'Bullish (long call)' : 'Bearish (long put)';
        }
        return 'Multi-leg';
      })();

  return (
    <div style={{
      background: c.card,
      border: `1px solid ${c.border}`,
      borderRadius: 8,
    }}>
      {/* Summary row */}
      <div
        onClick={onToggle}
        style={{
          padding: '0.85rem 1rem',
          cursor: 'pointer',
          display: 'grid',
          gridTemplateColumns: 'minmax(180px, 1fr) minmax(220px, 2fr) minmax(160px, 1fr) auto',
          gap: '1rem',
          alignItems: 'center',
        }}
      >
        {/* Symbol + strategy */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '1.05rem', fontWeight: 700, color: c.text }}>{signal.symbol}</span>
            <span style={{
              fontSize: '0.66rem',
              padding: '0.15rem 0.5rem',
              borderRadius: 10,
              background: sty.bg,
              color: sty.color,
              fontWeight: 700,
            }}>{strategy}</span>
          </div>
          {directionLabel && (
            <div style={{ fontSize: '0.74rem', color: c.muted }}>{directionLabel}</div>
          )}
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
            {brief && (
              <AIBriefTag brief={brief} open={briefOpen} onToggle={onBriefToggle} />
            )}
            {signal.timestamp && <QuoteFreshnessTag ts={signal.timestamp} />}
          </div>
        </div>

        {/* Key metrics */}
        <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
          {entryAmt > 0 && (
            <Cell
              label={isCredit ? 'Credit' : 'Debit'}
              value={`$${fmt(entryAmt, 2)}`}
              valueColor={isCredit ? c.green : c.red}
            />
          )}
          {car > 0 && (
            <Cell label="Max risk" value={`$${fmt(car, 0)}`} />
          )}
          {maxProfit > 0 && (
            <Cell label="Max gain" value={`$${fmt(maxProfit, 0)}`} valueColor={c.green} />
          )}
          {dte != null && (
            <Cell label="DTE" value={`${dte}d`} />
          )}
          {signal.expiration && (
            <Cell label="Expires" value={fmtDate(signal.expiration)} />
          )}
        </div>

        {/* Score */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.62rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Score</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: c.purple }}>
            {fmtSignedScore(signal.score)}
          </div>
        </div>

        {/* Expand chevron */}
        <div style={{ fontSize: '0.78rem', color: c.muted }}>
          {expanded ? '▼' : '▶'}
        </div>
      </div>

      {/* AI brief panel (independent of card expansion) */}
      {brief && briefOpen && <AIBriefPanel brief={brief} />}

      {/* Expanded detail */}
      {expanded && (
        <div style={{
          padding: '0 1rem 1rem',
          borderTop: `1px solid ${c.border}`,
          background: c.cardAlt,
        }}>
          {/* Legs table */}
          {legs.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', color: c.muted, marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Legs
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ color: c.muted, fontSize: '0.72rem' }}>
                    <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', fontWeight: 600 }}>Action</th>
                    <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', fontWeight: 600 }}>Type</th>
                    <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', fontWeight: 600 }}>Strike</th>
                    <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', fontWeight: 600 }}>Δ Delta</th>
                    <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', fontWeight: 600 }}>Mark</th>
                    <th style={{ padding: '0.35rem 0.4rem', textAlign: 'left', fontWeight: 600 }}>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {legs.map((leg, i) => {
                    const isShort = (leg.action || '').toLowerCase() === 'sell';
                    return (
                      <tr key={i} style={{ borderTop: `1px solid ${c.border}` }}>
                        <td style={{ padding: '0.4rem', color: isShort ? c.red : c.green, fontWeight: 700 }}>
                          {isShort ? 'SELL' : 'BUY'}
                        </td>
                        <td style={{ padding: '0.4rem' }}>{String(leg.type || '').toUpperCase()}</td>
                        <td style={{ padding: '0.4rem' }}>${fmt(leg.strike, 2)}</td>
                        <td style={{ padding: '0.4rem' }}>{fmt(leg.delta, 3)}</td>
                        <td style={{ padding: '0.4rem' }}>${fmt(leg.mark, 2)}</td>
                        <td style={{ padding: '0.4rem' }}>{leg.expiry ? fmtDate(leg.expiry) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Exit rules */}
          {Object.keys(exitRules).length > 0 && (
            <div style={{ marginTop: '0.85rem' }}>
              <div style={{ fontSize: '0.72rem', color: c.muted, marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Exit framework
              </div>
              <div style={{ fontSize: '0.82rem', color: c.text, lineHeight: 1.55 }}>
                {exitRules.profit_target_pct != null && (
                  <div>
                    <strong>Profit target:</strong> close when {Math.round(Number(exitRules.profit_target_pct) * 100)}% of credit is captured
                    {entryAmt > 0 && <span style={{ color: c.muted }}> (buy back at ${fmt(entryAmt * (1 - Number(exitRules.profit_target_pct)), 2)})</span>}
                  </div>
                )}
                {exitRules.time_exit_dte != null && (
                  <div><strong>Time exit:</strong> close at {exitRules.time_exit_dte} DTE remaining</div>
                )}
                {exitRules.naked_short_management && (
                  <div>
                    <strong>If short side breached:</strong> {exitRules.naked_short_management}
                    {exitRules.convert_trigger_credit_multiple != null && (
                      <span style={{ color: c.muted }}> (trigger at {fmt(exitRules.convert_trigger_credit_multiple)}× credit)</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Why this preset */}
          <div style={{
            marginTop: '0.85rem',
            padding: '0.55rem 0.7rem',
            background: c.card,
            border: `1px solid ${c.border}`,
            borderRadius: 6,
            fontSize: '0.78rem',
            color: c.muted,
            lineHeight: 1.55,
          }}>
            This signal scored highest under the <strong style={{ color: c.text }}>{PRESET_LABEL[(signal.preset as Preset) || 'balanced']}</strong> preset.
            Different presets weight the underlying scoring components differently
            (conservative favors probability-of-profit; aggressive favors return-on-risk).
          </div>

          {/* Premium chart — same component the v1 page uses. Renders when
              the API has attached premium_history snapshots. The Fargate
              collector starts writing v2 snapshots after the 2026-06-01
              deploy, so the chart will be empty for the first few hours
              while data accumulates. */}
          {(() => {
            const ph = signal.premium_history || [];
            if (ph.length < 2) {
              return (
                <div style={{
                  marginTop: '0.65rem',
                  padding: '0.65rem 0.7rem',
                  background: c.cardAlt,
                  border: `1px dashed ${c.border}`,
                  borderRadius: 6,
                  fontSize: '0.78rem',
                  color: c.dim,
                  fontStyle: 'italic',
                  lineHeight: 1.5,
                }}>
                  Premium chart pending — snapshot data is collected every 15 min during market hours.
                  Check back later for the time series.
                </div>
              );
            }
            // Compute chart inputs. For credit spreads the "entry premium" the
            // chart treats as the entry value is the spread credit (positive).
            // Profit target = entry * (1 - profit_target_pct). Stop ~ 2x entry.
            const isSelling = isCredit;
            const profitTargetPct = Number(exitRules.profit_target_pct ?? 0.5);
            const targetPremium = isSelling
              ? entryAmt * (1 - profitTargetPct)
              : entryAmt * (1 + profitTargetPct);
            const stopPremium = isSelling
              ? entryAmt * 2.0
              : Math.max(0, entryAmt * 0.5);
            return (
              <div style={{ marginTop: '0.85rem' }}>
                <div style={{ fontSize: '0.72rem', color: c.muted, marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Premium tracking
                </div>
                <OptionPremiumChart
                  premiumHistory={ph}
                  entryPremium={entryAmt}
                  targetPremium={targetPremium}
                  stopPremium={stopPremium}
                  isSelling={isSelling}
                  symbol={signal.symbol}
                />
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

const Cell: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div>
    <div style={{ fontSize: '0.62rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: valueColor ?? c.text, marginTop: 1 }}>{value}</div>
  </div>
);

export default TradeSignalsV2;
